
import React, { useState, useRef, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./SupplierForm.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlaneDeparture, faPlaneArrival, faCalendar, faUsers, faBed, faUtensils, faChevronDown, faChevronUp, faPlus, faMinus, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import airports from "../../Data/AirportList.json";
import moment from "moment";
import { useAuth } from "../../Common/Configurations/AuthContext";

// --- Reusable Modal component ---
// --- Reusable Modal component ---
const Modal = ({ isOpen, onClose, children, customClassName = '' }) => {
  if (!isOpen) return null;
  return (
    // ADD a specific class to the backdrop
    <div className={`custom-modal-backdrop ${customClassName}-backdrop`} onClick={onClose}>
      <div className={`modal-content ${customClassName}`} onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>&times;</button>
        {children}
      </div>
    </div>
  );
};

// Helper to get a clean city name for display
const getCleanCityName = (airport) => {
    if (!airport || !airport.cityName) return '';
    return airport.cityName.split(',')[0];
};

const AISupplierGeneratorForm = ({ onGenerate, isGenerating }) => {
  const [formData, setFormData] = useState({
    departureCity: null,
    arrivalCity: null,
    startDate: null,
    endDate: null,
    numAdults: 1,      // Default to 1
    numChildren: 0,   // Default to 0
    numInfants: 0,    // Default to 0
    includeFlights: true,
    numRooms: 1,
    hotelCategory: '4 Star',
    mealType: 'MAP',
    cabinClass: 'Economy',
    isDirectFLight: false,
    isDomestic: "DOMESTIC",
    journeyType: "RoundTrip",
    preferred: "REGULAR"
  });
  const { user, loading } = useAuth();
  const [departureResults, setDepartureResults] = useState([]);
  const [arrivalResults, setArrivalResults] = useState([]);
  const [displayFrom, setDisplayFrom] = useState('');
  const [displayTo, setDisplayTo] = useState('');
  const [cities, setCities] = useState([]); // Default to empty array
  const [isPassengerDropdownOpen, setPassengerDropdownOpen] = useState(false);
  const passengerRef = useRef(null);
  const [isCityModalOpen, setCityModalOpen] = useState(false);
  const [newCityName, setNewCityName] = useState('');
  const [newCityNights, setNewCityNights] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  const calculateTotalNights = () => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    }
    return 0;
  };

  const totalTripNights = calculateTotalNights();
  const allocatedNights = cities.reduce((total, city) => total + parseInt(city.nights || 0), 0);
  const areNightsBalanced = totalTripNights > 0 && totalTripNights === allocatedNights;

  useEffect(() => {
    function handleClickOutside(event) {
      if (passengerRef.current && !passengerRef.current.contains(event.target)) {
        setPassengerDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [passengerRef]);

  const handleDepartureAirportSearch = (e) => {
    const value = e.target.value;
    setDisplayFrom(value);
    if (value.length > 1) {
      const lowerValue = value.toLowerCase();
      const filtered = airports.filter(airport =>
        airport.cityName.toLowerCase().includes(lowerValue) ||
        airport.airportCode.toLowerCase().includes(lowerValue)
      ).slice(0, 7);
      setDepartureResults(filtered);
    } else {
      setDepartureResults([]);
    }
  };

  const handleArrivalAirportSearch = (e) => {
    const value = e.target.value;
    setDisplayTo(value);
    if (value.length > 1) {
      const lowerValue = value.toLowerCase();
      const filtered = airports.filter(airport =>
        airport.cityName.toLowerCase().includes(lowerValue) ||
        airport.airportCode.toLowerCase().includes(lowerValue)
      ).slice(0, 7);
      setArrivalResults(filtered);
    } else {
      setArrivalResults([]);
    }
  };

  const handleDepartureSelect = (airport) => {
    setFormData(prev => ({ ...prev, departureCity: airport }));
    setDisplayFrom(getCleanCityName(airport));
    setDepartureResults([]);
  };

  const handleArrivalSelect = (airport) => {
    setFormData(prev => ({ ...prev, arrivalCity: airport }));
    setDisplayTo(getCleanCityName(airport));
    setArrivalResults([]);
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: val }));
  };
  
  const handlePassengerChange = (type, delta) => {
    setFormData(prev => {
      const newValue = prev[type] + delta;
      if (type === 'numAdults' && newValue < 1) return prev;
      if (newValue < 0) return prev;
      return { ...prev, [type]: newValue };
    });
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setFormData(prev => ({ ...prev, startDate: start, endDate: end }));
  };

  const removeCity = (indexToRemove) => {
    setCities(cities.filter((_, index) => index !== indexToRemove));
  };

  const handleAddCity = () => {
    const nightsToAdd = parseInt(newCityNights, 10);
    if (newCityName.trim() && nightsToAdd > 0) {
      if (allocatedNights + nightsToAdd > totalTripNights) {
        alert(`Cannot add ${nightsToAdd} nights. Only ${totalTripNights - allocatedNights} nights are remaining for this trip.`);
        return;
      }
      setCities([...cities, { name: newCityName.trim(), nights: nightsToAdd }]);
      setNewCityName('');
      setNewCityNights('');
      setCityModalOpen(false);
    }
  };
  
  const passengerDisplayText = `${formData.numAdults} Adults, ${formData.numChildren} Children, ${formData.numInfants} Infants`;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- Start: Input Validation (Good practice) ---
    if (!formData.departureCity || !formData.arrivalCity) {
      alert("Please select departure and arrival cities.");
      return;
    }
    if (cities.length === 0) {
      alert("Please add at least one city to visit.");
      return;
    }
    if (!areNightsBalanced) {
      alert(`The total nights allocated to cities (${allocatedNights}) must exactly match the total trip duration of ${totalTripNights} nights. Please adjust.`);
      return;
    }
    // --- End: Input Validation ---

    // 1. **FORMAT THE PAYLOAD** to match the C# AIUserRequest model
   const payload = {
         packageType: "Flight",
         fromDestination: formData.departureCity.airportCode,
         toDestination: formData.arrivalCity.airportCode,
         destination: formData.arrivalCity.airportCode,
         departureDate: moment(formData.startDate).format("YYYY-MM-DD"),
         returnDate: moment(formData.endDate).format("YYYY-MM-DD"),
         adults: formData.numAdults,
         children: formData.numChildren,
         infants: formData.numInfants,
         numberOfPeople: formData.numAdults + formData.numChildren + formData.numInfants,
         currency: "USD", 
         numberOfNights: totalTripNights,
         placesToCover: cities.map(city => ({
           name: city.name,
           nights: city.nights
         })),
         companyId:  user?.cid,
         userId: user?.id,
         cabinClass: 'Economy',
         isDirectFlight: false,
         isDomestic: "DOMESTIC",
         journeyType: "ROUNDTRIP",
         preferred: "REGULAR"
       };

    // 2. **CALL THE onGenerate FUNCTION** from the parent component, passing the payload.
    //    The actual API call will happen in the parent.
    onGenerate(payload);
  };

  return (
    <>
      <div className="ai-generator-container">
        <h2 className="ai-generator-title">Generate Itinerary with AI</h2>
        <p className="ai-generator-subtitle">
          Tell us your travel preferences, and our AI will craft the perfect itinerary for you.
        </p>
        <form onSubmit={handleSubmit} className="ai-form">
          <div className="row">
            <div className="col-3">
              <div className="form-group">
                <label><FontAwesomeIcon icon={faPlaneDeparture} /> Departure City</label>
                <input
                  type="text"
                  name="departureCity"
                  placeholder="e.g., Mumbai"
                  value={displayFrom}
                  onChange={handleDepartureAirportSearch}
                  autoComplete="off"
                  required={!formData.departureCity}
                />
                {departureResults.length > 0 && (
                  <div className="autocomplete-suggestions">
                    {departureResults.map(airport => (
                      <div key={airport.airportCode} className="suggestion-item" onClick={() => handleDepartureSelect(airport)}>
                        <strong>{getCleanCityName(airport)} ({airport.airportCode}) - {airport.countryName}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col-3">
              <div className="form-group">
                <label><FontAwesomeIcon icon={faPlaneArrival} /> Arrival City</label>
                <input
                  type="text"
                  name="arrivalCity"
                  placeholder="e.g., Himachal"
                  value={displayTo}
                  onChange={handleArrivalAirportSearch}
                  autoComplete="off"
                  required={!formData.arrivalCity}
                />
                {arrivalResults.length > 0 && (
                  <div className="autocomplete-suggestions">
                    {arrivalResults.map(airport => (
                      <div key={airport.airportCode} className="suggestion-item" onClick={() => handleArrivalSelect(airport)}>
                          <strong>{getCleanCityName(airport)} ({airport.airportCode}) - {airport.countryName}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col-3"><div className="form-group"><label><FontAwesomeIcon icon={faCalendar} /> Travel Dates</label><DatePicker selectsRange={true} startDate={formData.startDate} endDate={formData.endDate} onChange={handleDateChange} dateFormat="dd-MMM-yyyy" placeholderText="Select start & end dates" className="date-range-picker" required /></div></div>
            <div className="col-3"><div className="form-group passenger-selector" ref={passengerRef}><label><FontAwesomeIcon icon={faUsers} /> Passengers</label><div className="passenger-display" onClick={() => setPassengerDropdownOpen(!isPassengerDropdownOpen)}>{passengerDisplayText}</div>{isPassengerDropdownOpen && (<div className="passenger-dropdown"><div className="passenger-row"><span className="passenger-label">Adults</span><div className="passenger-controls"><button type="button" className="control-btn" onClick={() => handlePassengerChange('numAdults', -1)}><FontAwesomeIcon icon={faMinus} /></button><span>{formData.numAdults}</span><button type="button" className="control-btn" onClick={() => handlePassengerChange('numAdults', 1)}><FontAwesomeIcon icon={faPlus} /></button></div></div><div className="passenger-row"><span className="passenger-label">Children</span><div className="passenger-controls"><button type="button" className="control-btn" onClick={() => handlePassengerChange('numChildren', -1)}><FontAwesomeIcon icon={faMinus} /></button><span>{formData.numChildren}</span><button type="button" className="control-btn" onClick={() => handlePassengerChange('numChildren', 1)}><FontAwesomeIcon icon={faPlus} /></button></div></div><div className="passenger-row"><span className="passenger-label">Infants</span><div className="passenger-controls"><button type="button" className="control-btn" onClick={() => handlePassengerChange('numInfants', -1)}><FontAwesomeIcon icon={faMinus} /></button><span>{formData.numInfants}</span><button type="button" className="control-btn" onClick={() => handlePassengerChange('numInfants', 1)}><FontAwesomeIcon icon={faPlus} /></button></div></div></div>)}</div></div>
          </div>

          <div className="form-group-full">
            <div className="label-with-info">
              <label>Cities & Nights</label>
              {totalTripNights > 0 && (
                <span className={`nights-info ${areNightsBalanced ? 'balanced' : 'unbalanced'}`}>
                  {areNightsBalanced ? `${allocatedNights} / ${totalTripNights} Nights Allocated` : 
                  <><FontAwesomeIcon icon={faExclamationCircle} /> {allocatedNights} of {totalTripNights} Nights Allocated</>
                  }
                </span>
              )}
            </div>
            <div className="city-chip-container">
              {cities.map((city, index) => (
                <div key={index} className="city-chip">
                  <span>{city.name} ({city.nights} Nights)</span>
                  <button type="button" className="remove-chip-btn" onClick={() => removeCity(index)}>&times;</button>
                </div>
              ))}
              <button type="button" className="add-city-placeholder-btn" onClick={() => setCityModalOpen(true)}>
                <FontAwesomeIcon icon={faPlus} /> Add City
              </button>
            </div>
          </div>
          
          <div className="advanced-options-toggle">
              <button type="button" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}>
                  Advanced Options 
                  <FontAwesomeIcon icon={showAdvancedOptions ? faChevronUp : faChevronDown} />
              </button>
          </div>

          <div className={`advanced-options-container ${showAdvancedOptions ? 'show' : ''}`}>
              <div className="row">
                  <div className="col-3">
                      <div className="form-group">
                          <label><FontAwesomeIcon icon={faBed} /> Hotel Category</label>
                          <select name="hotelCategory" value={formData.hotelCategory} onChange={handleInputChange}>
                              <option>3 Star</option>
                              <option>4 Star</option>
                              <option>5 Star</option>
                          </select>
                      </div>
                  </div>
                  <div className="col-3">
                      <div className="form-group">
                          <label><FontAwesomeIcon icon={faUtensils} /> Meal Type</label>
                          <select name="mealType" value={formData.mealType} onChange={handleInputChange}>
                              <option value="CP">Breakfast Only</option>
                              <option value="MAP">Breakfast & Dinner</option>
                              <option value="AP">All Meals</option>
                          </select>
                      </div>
                  </div>
                  <div className="col-3">
                      <div className="form-group">
                          <label><FontAwesomeIcon icon={faBed} /> No. of Rooms</label>
                          <input type="number" name="numRooms" value={formData.numRooms} onChange={handleInputChange} min="1"/>
                      </div>
                  </div>
                  <div className="col-3">
                      <div className="form-group checkbox-group">
                         <label htmlFor="includeFlights">
                             <input type="checkbox" id="includeFlights" name="includeFlights" checked={formData.includeFlights} onChange={handleInputChange} />
                             Include Flights?
                         </label>
                      </div>
                  </div>
              </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="generate-btn" disabled={isGenerating || !areNightsBalanced}>
              {isGenerating ? "Generating..." : "✨ Generate Itinerary"}
            </button>
          </div>
        </form>
      </div>

      <Modal 
    isOpen={isCityModalOpen} 
    onClose={() => setCityModalOpen(false)}
    customClassName="add-city-modal" 
>
        <h3 className="modal-title">Add a City</h3>
        <div className="modal-form-group">
          <label>City Name</label>
          <input 
            type="text" 
            placeholder="e.g., Paris"
            value={newCityName}
            onChange={e => setNewCityName(e.target.value)}
          />
        </div>
        <div className="modal-form-group">
          <label>Number of Nights</label>
          <input 
            type="number"
            placeholder="e.g., 3"
            value={newCityNights}
            onChange={e => setNewCityNights(e.target.value)}
            min="1"
          />
        </div>
        <button 
          type="button" 
          className="modal-add-btn" 
          onClick={handleAddCity}
          disabled={!newCityName.trim() || !newCityNights || allocatedNights + parseInt(newCityNights || 0) > totalTripNights}
        >
          Add City
        </button>
      </Modal>
    </>
  );
};

export default AISupplierGeneratorForm;